import { applyDelta, blankRelationship, deltaFloaters, sortNewestFirst, splitForCondense } from '../../sim/relationships.js';
import { breakRangeMs, rollMs } from '../../sim/conversationPace.js';
import type { Memory, Npc, Relationship } from '../../types.js';
import { fetchDelta } from '../useConversations.js';
import type { ActiveConvo } from '../useConversations.js';
import { uid } from '../spawn.js';
import type { WorldState } from '../useSim.js';
import { activatableDeviceIds } from '../../runtime/deviceScheduler.js';
import { appendHistoryEvent, cliffNoteHistoryEvent } from './gameIntegration.js';
import type { PersistedRuntimeState } from './types.js';
import type { WorldLaunchHandoff } from '../CreateWorld/draftStorage.js';
import type { PlotDevice } from '../../domain/types.js';

const relKey = (a: string, b: string): string => `${a}>${b}`;

export async function applyConversationEnd(args: {
  convo: ActiveConvo;
  world: WorldState;
  npcById: (id: string) => Npc;
  rels: Map<string, Relationship>;
  mems: Memory[];
  long: Map<string, string>;
  floaters: Array<{ id: string; npcId: string; items: Array<{ label: string; up: boolean }>; born: number }>;
  onNpcEnergy: (updater: (list: Npc[]) => Npc[]) => void;
  onForceRender: () => void;
  aquarium?: {
    handoff: WorldLaunchHandoff;
    runtimeState: PersistedRuntimeState;
    activeDevices: PlotDevice[];
    setRuntimeState: React.Dispatch<React.SetStateAction<PersistedRuntimeState | null>>;
  };
}): Promise<void> {
  const { convo, world, npcById, rels, mems, long, floaters, onNpcEnergy, onForceRender, aquarium } = args;
  const failed = convo.id === 'failed' || convo.lines.length === 0;
  const pauseUntil = world.now + (failed ? 30_000 : rollMs(breakRangeMs));
  for (const rt of world.runtime) {
    if (rt.npcId !== convo.aId && rt.npcId !== convo.bId) continue;
    rt.busyWith = null;
    rt.moving = true;
    rt.cooldownUntil = pauseUntil;
    rt.retargetAt = 0;
  }
  world.quietUntil = pauseUntil;
  if (failed) return;

  const drain = Math.max(1, Math.round(convo.lines.length / 6));
  onNpcEnergy((list) =>
    list.map((n) =>
      n.id === convo.aId || n.id === convo.bId
        ? { ...n, state: { ...n.state, energy: Math.max(1, n.state.energy - drain) } }
        : n,
    ),
  );

  if (aquarium && !failed) {
    const a = npcById(convo.aId);
    const b = npcById(convo.bId);
    const evt = await cliffNoteHistoryEvent({
      worldId: aquarium.handoff.snapshot.worldId,
      occurredAt: aquarium.runtimeState.clock.inWorldIso,
      a,
      b,
      lines: convo.lines,
      deviceIds: activatableDeviceIds(
        aquarium.handoff.snapshot.schedule,
        aquarium.runtimeState.clock.inWorldIso,
        aquarium.activeDevices.map((d) => d.deviceId),
      ),
    });
    if (evt) aquarium.setRuntimeState((s) => (s ? appendHistoryEvent(s, evt) : s));
  }

  try {
    const a = npcById(convo.aId);
    const b = npcById(convo.bId);
    const delta = (await fetchDelta(a, b, convo.lines)) as {
      a_to_b: { familiarity: number; affinity: number; trust: number; respect: number; fear: number; attraction: number };
      b_to_a: { familiarity: number; affinity: number; trust: number; respect: number; fear: number; attraction: number };
      memories: Array<{ from: 'a' | 'b'; summary: string; salience: number }>;
    };
    const sides: Array<[string, string, typeof delta.a_to_b]> = [
      [convo.aId, convo.bId, delta.a_to_b],
      [convo.bId, convo.aId, delta.b_to_a],
    ];
    for (const [from, to, d] of sides) {
      const cur = rels.get(relKey(from, to)) ?? blankRelationship(from, to);
      rels.set(relKey(from, to), applyDelta(cur, d));
    }
    for (const [from, , d] of sides) {
      const items = deltaFloaters(d);
      if (items.length > 0) {
        floaters.push({
          id: uid('fl'), npcId: from, born: world.now,
          items: items.map((i) => ({ label: `${i.delta > 0 ? '+' : ''}${i.delta} ${i.field}`, up: i.delta > 0 })),
        });
      }
    }
    for (const m of delta.memories.slice(0, 4)) {
      const from = m.from === 'a' ? convo.aId : convo.bId;
      const to = m.from === 'a' ? convo.bId : convo.aId;
      mems.push({
        memoryId: uid('mem'), npcId: from, targetId: to,
        summary: m.summary.slice(0, 160), salience: m.salience,
        affinityDelta: 0, trustDelta: 0, respectDelta: 0, fearDelta: 0, attractionDelta: 0,
        createdAt: new Date().toISOString(),
      });
    }
    for (const [from, to] of [[convo.aId, convo.bId], [convo.bId, convo.aId]] as Array<[string, string]>) {
      const all = sortNewestFirst(mems.filter((x) => x.npcId === from && x.targetId === to));
      const { kept, bumped } = splitForCondense(all);
      if (bumped.length > 0) {
        const prev = long.get(relKey(from, to)) ?? '';
        const add = bumped.map((x) => x.summary).join(' ');
        long.set(relKey(from, to), `${prev} ${add}`.trim().split(/\s+/).slice(-120).join(' '));
        const keepIds = new Set(kept.map((x) => x.memoryId));
        for (let i = mems.length - 1; i >= 0; i--) {
          const x = mems[i]!;
          if (x.npcId === from && x.targetId === to && !keepIds.has(x.memoryId)) mems.splice(i, 1);
        }
      }
    }
    onForceRender();
  } catch {
    // Delta failure is non-fatal.
  }
}
